import { useAuth } from "@clerk/clerk-react";
import {
  Divider,
  Drawer,
  Grid,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { DiscoverTabView, DiscoverViewContext } from "contexts/DiscoverView";
import { OrganizationUserContext } from "contexts/Organization";
import { TrackedCreatorsContext } from "contexts/TrackedCreators";
import { useContext, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { TrackedCreatorWrapper } from "schemas/dashboard";

import TrackedCreatorSideBar from "features/Home/Sidebar/TrackCreatorsSideBar";
import { fetcherAuth } from "utils/api";

import CreatorCard from "./CreatorCard";

const PER_PAGE = 20;
const REF_OFFSET = 9;

export default function TrackCreators() {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));

  const { currentOrg } = useContext(OrganizationUserContext);
  const { setDiscoverTab, discoverTab } = useContext(DiscoverViewContext);
  const {
    trackedCreators,
    setTrackedCreators,
    setTrackedCreatorSideBar,
    trackedCreatorSideBar,
  } = useContext(TrackedCreatorsContext);
  const { setErrorAlert } = useContext(AlertContext);
  const { getToken } = useAuth();
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<number | null>(null);

  const [paginationRef, isPaginationRefVisible] = useInView({
    rootMargin: "0px 0px",
  });

  const fetchTrackedCreators = async (cursor?: number) => {
    if (!currentOrg?.id) return;
    if (cursor) {
      setLoading(true);
    } else {
      setInitialLoading(true);
    }

    let url = `/api/organization/${currentOrg.id}/tracked-creators?per_page=${PER_PAGE}`;

    if (cursor) {
      url += `&cursor=${JSON.stringify([cursor])}`;
    }

    try {
      const { trackedCreators, total } = await fetcherAuth(
        getToken,
        url,
        "GET",
        {},
      );
      if (cursor) {
        setTrackedCreators((prev) => [...prev, ...trackedCreators]);
      } else {
        setTrackedCreators(trackedCreators);
      }
      setTotal(total);
    } catch (error: any) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const editTrackedCreator = async (
    trackedCreatorId: number,
    newAutoTrackValue: boolean,
  ) => {
    if (!currentOrg?.id) return;

    // optimistically update
    setTrackedCreators((prev) =>
      prev.map((trackedCreatorWrapper) => {
        if (trackedCreatorWrapper.trackedCreator.id === trackedCreatorId) {
          trackedCreatorWrapper.trackedCreator.autoTrackingEnabled =
            newAutoTrackValue;
        }
        return trackedCreatorWrapper;
      }),
    );

    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/tracked-creators/${trackedCreatorId}`,
        "PUT",
        {},
        {
          autoTrackingEnabled: newAutoTrackValue,
        },
      );
    } catch (error: any) {
      setErrorAlert(error);
    }
  };

  const archiveTrackedCreator = async (trackedCreatorId: number) => {
    if (!currentOrg?.id) return;

    // optimistically remove from list
    setTrackedCreators((prev) =>
      prev.filter(
        (trackedCreatorWrapper) =>
          trackedCreatorWrapper.trackedCreator.id !== trackedCreatorId,
      ),
    );
    setTotal((prev) => (prev ? prev - 1 : prev));

    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/tracked-creators/${trackedCreatorId}`,
        "PUT",
        {},
        {
          isArchived: true,
        },
      );
    } catch (error: any) {
      setErrorAlert(error);
    }
  };

  useEffect(() => {
    fetchTrackedCreators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    if (
      isPaginationRefVisible &&
      trackedCreators &&
      total &&
      trackedCreators.length < total &&
      !loading
    ) {
      fetchTrackedCreators(
        trackedCreators[trackedCreators.length - 1].trackedCreator.createdAt,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaginationRefVisible, trackedCreators?.length]);

  return (
    <>
      <Grid item xs={12} sx={{ p: 2 }}>
        <Typography gutterBottom variant="h3">
          Tracked Creators
        </Typography>
        <Typography paragraph>
          Monitor who they collaborate with and auto-add their brands to your
          pitch list
        </Typography>
        <Divider />
      </Grid>

      <Grid
        item
        container
        xs={12}
        spacing={2}
        sx={{ py: 2, px: { xs: 0, md: 2 }, mb: 10 }}
      >
        {!initialLoading &&
          trackedCreators.map((wrapper, index) => (
            <Grid
              ref={
                (
                  REF_OFFSET > trackedCreators.length
                    ? trackedCreators.length - 1 === index
                    : trackedCreators.length - REF_OFFSET === index
                )
                  ? paginationRef
                  : null
              }
              key={wrapper.trackedCreator.id}
              item
              xs={12}
            >
              <CreatorCard
                creatorWrapper={wrapper}
                onClick={(wrapper: TrackedCreatorWrapper) => {
                  if (
                    trackedCreatorSideBar?.trackedCreator.id ===
                    wrapper.trackedCreator.id
                  ) {
                    setTrackedCreatorSideBar(null);
                    setDiscoverTab(null);
                  } else {
                    setTrackedCreatorSideBar(wrapper);
                    if (discoverTab !== DiscoverTabView.SAVED_BRANDS) {
                      setDiscoverTab(DiscoverTabView.SAVED_BRANDS);
                    }
                  }
                }}
                updateAutoTrack={(
                  trackedCreatorId: number,
                  newValue: boolean,
                ) => {
                  editTrackedCreator(trackedCreatorId, newValue);
                }}
                onArchive={(trackedCreatorId: number) => {
                  archiveTrackedCreator(trackedCreatorId);
                }}
              />
            </Grid>
          ))}
        {(initialLoading || loading) && (
          <>
            <Grid item xs={12}>
              <Skeleton
                animation="wave"
                variant="rounded"
                height={87.5}
                width="100%"
              />
            </Grid>
            <Grid item xs={12}>
              <Skeleton
                animation="wave"
                variant="rounded"
                height={87.5}
                width="100%"
              />
            </Grid>
            <Grid item xs={12}>
              <Skeleton
                animation="wave"
                variant="rounded"
                height={87.5}
                width="100%"
              />
            </Grid>
          </>
        )}
      </Grid>

      {isMobileScreen && (
        <Drawer
          sx={{
            width: {
              xs: "100%",
              sm: "400px",
            },
            top: 0,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              border: "none",
              top: 0,
              width: {
                xs: "100%",
                sm: "400px",
              },
              overflowY: "hidden",
            },
          }}
          anchor="right"
          onClose={() => {
            setTrackedCreatorSideBar(null);
            setDiscoverTab(null);
          }}
          open={!!trackedCreatorSideBar}
        >
          {trackedCreatorSideBar && <TrackedCreatorSideBar />}
        </Drawer>
      )}
    </>
  );
}
